import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Building2, Lock, User, Eye, EyeOff } from "lucide-react";
import { useLogo } from "@/lib/useLogo";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const logo = useLogo();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate("/");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="hidden md:flex relative overflow-hidden bg-primary text-primary-foreground">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1554469384-e58fac16e23a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZmluYW5jaWFsfGVufDB8fHx8MTc4NDM5MTA3MHww&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 to-blue-900/90" />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="Nayan Services"
              className="w-11 h-11 rounded-xl object-cover border border-white/25 shadow"
            />
            <div>
              <div className="font-heading font-bold text-lg">NAYAN SERVICES</div>
              <div className="text-xs opacity-80">CRM · Enterprise Suite</div>
            </div>
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-4xl leading-tight mb-4">
              Manage Tax, GST, Loan &<br />Financial Services with ease.
            </h1>
            <p className="opacity-85 max-w-md text-sm leading-relaxed">
              A premium CRM built for financial services teams — track leads, manage
              connectors, monitor documents, and communicate in real-time.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                ["Real-time", "Chat & Notify"],
                ["Secure", "Encrypted Storage"],
                ["Reports", "Excel · PDF · CSV"],
              ].map(([a, b], i) => (
                <div key={i} className="bg-white/10 border border-white/15 rounded-lg p-3">
                  <div className="font-heading font-bold text-sm">{a}</div>
                  <div className="text-[11px] opacity-75">{b}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs opacity-60">© {new Date().getFullYear()} NAYAN SERVICES CRM</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 md:hidden flex items-center gap-3">
            <img src={logo} alt="Nayan Services" className="w-10 h-10 rounded-lg object-cover shadow" />
            <div className="font-heading font-bold">NAYAN SERVICES CRM</div>
          </div>
          <h2 className="font-heading text-3xl font-extrabold mb-1">Sign in to NAYAN SERVICES CRM</h2>
          <p className="text-sm text-muted-foreground mb-8">Enter your credentials to access your dashboard.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Username</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  className="input pl-9"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  data-testid="login-username-input"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  className="input pl-9 pr-9"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                  required
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1} data-testid="login-toggle-show">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full !py-3 mt-2"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 p-4 rounded-lg bg-muted/60 border border-border text-xs text-muted-foreground">
            <div className="font-semibold text-foreground mb-1 flex items-center gap-2">
              <Building2 size={14} /> Admin access
            </div>
            Default admin credentials are configured in your environment. Contact your administrator if you need access.
          </div>
        </div>
      </div>
    </div>
  );
}
